import { useEffect } from 'react';

/**
 * SEOHead Component
 * Dynamically updates meta tags for SEO and social media sharing
 */
const SEOHead = ({ 
  title, 
  description, 
  image = '/images/shared-oxygen-linkedin.png',
  imageAlt,
  imageWidth = '1200',
  imageHeight = '630',
  url, 
  type = 'article',
  author = 'Shared Oxygen',
  publishedTime,
  modifiedTime,
  tags = []
}) => {
  useEffect(() => {
    const metaTitle = title?.includes('Shared Oxygen') ? title : `${title} | Shared Oxygen`;

    // Update document title
    if (title) {
      document.title = metaTitle;
    }

    // Helper to update or create meta tag
    const updateMetaTag = (property, content, isName = false) => {
      if (!content) return;
      
      const attribute = isName ? 'name' : 'property';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    // Determine the correct image MIME type
    const deriveImageType = (url) => {
      if (!url) return null;
      const parsed = url.split('?')[0].toLowerCase();
      if (parsed.endsWith('.svg')) return 'image/svg+xml';
      if (parsed.endsWith('.png')) return 'image/png';
      if (parsed.endsWith('.gif')) return 'image/gif';
      if (parsed.endsWith('.webp')) return 'image/webp';
      if (parsed.endsWith('.avif')) return 'image/avif';
      return 'image/jpeg';
    };

    const runtimeOrigin = typeof window !== 'undefined' && window.location?.origin;
    const siteOrigin =
      import.meta.env?.VITE_SITE_URL ||
      (import.meta.env?.PROD ? 'https://sharedoxygen.com' : runtimeOrigin) ||
      'https://sharedoxygen.com';

    const toAbsoluteUrl = (path) => {
      if (!path) return null;
      return path.startsWith('http') ? path : `${siteOrigin}${path}`;
    };

    const resolveOgImage = (path) => {
      const absolute = toAbsoluteUrl(path);
      if (!absolute) return { og: null, original: null };

      if (absolute.toLowerCase().includes('.svg')) {
        const pngCandidate = absolute.replace(/\.svg(\?.*)?$/i, '.png$1');
        return { og: pngCandidate, original: absolute };
      }

      return { og: absolute, original: absolute };
    };

    // Basic meta tags
    updateMetaTag('description', description, true);
    
    // Open Graph meta tags (Facebook, LinkedIn, etc.)
    updateMetaTag('og:title', metaTitle);
    updateMetaTag('og:description', description);
    updateMetaTag('og:type', type);
    updateMetaTag('og:url', url || window.location.href);
    updateMetaTag('og:site_name', 'Shared Oxygen');
    
    // Image tags must be absolute for LinkedIn and other social crawlers.
    if (image) {
      const { og: ogImageUrl, original: originalImage } = resolveOgImage(image);
      const metaImage = ogImageUrl || originalImage;

      if (metaImage) {
        updateMetaTag('og:image', metaImage);
        updateMetaTag('og:image:secure_url', metaImage);
        updateMetaTag('og:image:width', imageWidth);
        updateMetaTag('og:image:height', imageHeight);
        const imageType = deriveImageType(metaImage);
        updateMetaTag('og:image:type', imageType);
        const resolvedImageAlt = imageAlt || title || 'Shared Oxygen';
        updateMetaTag('og:image:alt', resolvedImageAlt);

        // Twitter cards
        updateMetaTag('twitter:image', metaImage, true);
        updateMetaTag('twitter:image:alt', resolvedImageAlt, true);
      }
    }

    // Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', metaTitle, true);
    updateMetaTag('twitter:description', description, true);

    // Article specific meta tags
    if (type === 'article') {
      updateMetaTag('article:author', author);
      if (publishedTime) {
        updateMetaTag('article:published_time', publishedTime);
      }
      if (modifiedTime) {
        updateMetaTag('article:modified_time', modifiedTime);
      }
      
      // Article tags
      if (tags.length > 0) {
        // Remove existing article:tag meta tags
        document.querySelectorAll('meta[property="article:tag"]').forEach(el => el.remove());
        
        // Add new tags
        tags.forEach(tag => {
          const tagElement = document.createElement('meta');
          tagElement.setAttribute('property', 'article:tag');
          tagElement.setAttribute('content', tag);
          document.head.appendChild(tagElement);
        });
      }
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', url || window.location.href);

  }, [title, description, image, imageAlt, imageWidth, imageHeight, url, type, author, publishedTime, modifiedTime, tags]);

  return null; // This component doesn't render anything
};

export default SEOHead;

